<template>
  <div class="mx-auto max-w-content py-12">
    <div class="flex flex-wrap gap-y-8 px-sidespacing">
      <a
        v-for="[id, link] in linkMap"
        :href="link.href"
        :key="id"
        class="ankerlink link block w-full min-w-[243px] basis-full pr-8 sm:basis-1/5"
        @click="scrollTo(id, $event)"
      >
        <span class="text-primary">></span>
        <span class="link-text text-black">{{ link.text }}</span>
      </a>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import { trackEvent } from '../utils/tracking';

const linkMap = ref<Map<string, { text: string; href: string }>>(new Map());

onMounted(() => {
  const ankerlinks = document.querySelectorAll(
    'h2.anchor',
  ) as NodeListOf<HTMLElement>;
  const idCounts = new Map<string, number>();

  ankerlinks.forEach((ankerlink) => {
    const baseId =
      ankerlink.textContent
        ?.toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || '';

    const parentSection = parentsUntil(ankerlink, '.content');

    if (baseId && parentSection) {
      const count = idCounts.get(baseId) || 0;
      idCounts.set(baseId, count + 1);

      const uniqueId = count > 0 ? `${baseId}-${count + 1}` : baseId;

      const url = new URL(window.location.href);
      url.hash = uniqueId;

      parentSection.id = uniqueId;
      linkMap.value.set(uniqueId, {
        text: ankerlink.textContent || '',
        href: url.toString(),
      });
    }
  });
});

const parentsUntil = (
  el: HTMLElement,
  selector: string,
): HTMLElement | null => {
  let parent: HTMLElement | null = el;
  while (parent) {
    if (parent.matches(selector)) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
};

const scrollTo = (anchorId: string, $event: MouseEvent) => {
  $event.preventDefault();
  trackEvent('Anchor Link', 'Click', linkMap.value.get(anchorId)?.text || anchorId);
  document.querySelector(`#${anchorId}`)?.scrollIntoView({
    behavior: 'smooth',
  });
};
</script>

<style lang="scss" scoped>
.ankerlink {
  @apply overpass-text flex items-start gap-2 text-lg font-bold;
}
</style>
